// Public, unauthenticated donor donation page helpers. This is the first
// unauthenticated finance surface: the subsidiary rides in the route, there are
// no auth/tenant headers, and the server sends only branding + the required
// attestation key set (no $/PII).
//
// The attestation copy is per-KEY: the server sends which keys are required for
// the campaign's jurisdiction (federal / Alabama / else none), and the FE renders
// each key's copy at the render boundary via `attestationCopy` — the same
// engineer-key → donor-copy translation pattern as `stripeConnectPanelCopy`.

import { ElectionDesignation } from '../generated/enums.gen';

/**
 * The whole-cent amount to charge so that, after the processor's cut
 * (round(feeRate × charged) + feeFlatCents), the committee nets AT LEAST the intended gift. Mirrors
 * the backend `DonationChargeService.GrossUpToCoverFees` exactly, so the total shown to the donor
 * equals what the charge endpoint will actually charge. `intendedCents` is the gift the donor typed.
 */
export function coverFeesChargeCents(
  intendedCents: number,
  feeRate: number,
  feeFlatCents: number,
): number {
  let gross = Math.round((intendedCents + feeFlatCents) / (1 - feeRate));
  while (gross - (Math.round(feeRate * gross) + feeFlatCents) < intendedCents) {
    gross++;
  }
  return gross;
}

// PLACEHOLDER pilot copy — counsel-confirmed attestation wording is a later commit.
export const DONATION_ATTESTATION_COPY: Record<string, string> = {
  own_funds:
    'This contribution is made from my own funds and will not be reimbursed by anyone.',
  us_citizen_or_lpr:
    'I am a U.S. citizen or a lawfully admitted permanent resident.',
  at_least_18: 'I am at least eighteen years old.',
  not_federal_contractor: 'I am not a federal government contractor.',
  personal_card:
    'This contribution is made on a personal card, not a corporate or business account.',
  not_in_name_of_another:
    'This contribution is made in my own name and not in the name of another person.',
  own_free_will: 'This contribution is made of my own free will.',
};

/**
 * Render-boundary helper: translate a server-sent attestation key into donor
 * copy. Falls back to the raw key if an unknown key ever arrives (so a future
 * server-side key never renders a blank checkbox).
 */
export function attestationCopy(key: string): string {
  return DONATION_ATTESTATION_COPY[key] ?? key;
}

// PLACEHOLDER pilot copy — counsel-confirmed recurring-disclosure wording is a later commit.
//
// The recurring end condition is stated RACE-RELATIVE, not as a hard calendar stop date (ADR-076):
// the cutoff now deliberately over-runs the last known election by a uniform pad so a monthly gift
// carries the donor through a possible runoff. Promising a specific `recurringCutoff` date would
// under-state when the gift actually ends, so the donor is told it continues through the race. The
// `recurringCutoff` field stays on PublicDonationPageDto for the server's internal cutoff math; it is
// no longer surfaced to the donor. The backend receipt (DonorReceiptEmail) states the same end
// condition in its own copy — this constant is the donation form's render-boundary source, mirroring
// the DONATION_ATTESTATION_COPY pattern.
export const RECURRING_DONATION_DISCLOSURE =
  "Your monthly gift continues through the candidate's race, including any runoff, until the race concludes.";

// Shown under the employment fields on the donor form. Campaign-finance law requires
// occupation/employer collection; the self-employed guidance heads off the most common
// blank-employer case. Render-boundary copy, mirroring the disclosures above.
export const EMPLOYMENT_DISCLOSURE =
  'Campaign finance law requires us to collect contributor information, including employment. If you are self-employed, enter your own name as your employer.';

// The occupation/employer value submitted when a donor marks themselves retired or not
// currently employed — a single recognized reporting value in place of a free-text entry.
export const NOT_EMPLOYED_VALUE = 'Not employed';

/**
 * Donor-facing name for an election designation (GitHub #729 item 1).
 *
 * `Primary` / `General` / `Runoff` are OUR vocabulary — regulatory bucket names a donor has no
 * reason to know. On the donor page the designation is stated in a sentence ("Your gift supports
 * the primary election"), so it needs to read as English rather than as an enum value. The
 * treasurer-facing surfaces keep their own labels; this one is for people giving money.
 *
 * Takes the raw wire string rather than the enum, and degrades an unrecognized value to a truthful
 * vaguer phrase rather than echoing the raw code — a donor should never be shown an identifier we
 * failed to translate. That is why there is no `assertNever` here: the exhaustive-switch rule
 * governs a genuinely enum-typed value, and this one is server-sent text that must tolerate a
 * designation this build has never heard of. Same posture as the sibling `electionDesignationLabel`.
 */
export function donorElectionLabel(designation: string): string {
  switch (designation) {
    case ElectionDesignation.Primary:
      return 'the primary election';
    case ElectionDesignation.General:
      return 'the general election';
    case ElectionDesignation.Runoff:
      return 'the runoff election';
    default:
      return 'the upcoming election';
  }
}
